import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Lock, Check, X, Loader2, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {label}
    </li>
  );
}

export default function ForcePasswordChangePage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const checks = useMemo(() => ({
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
    match: newPassword.length > 0 && newPassword === confirm,
    different: newPassword !== currentPassword && newPassword.length > 0,
  }), [newPassword, confirm, currentPassword]);

  const allOk = Object.values(checks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated. Welcome aboard.");
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex crew-login-hero items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Anchor className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>CrewOn</div>
        </div>

        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-sm">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Set a new password to continue</div>
            <div className="text-xs mt-0.5">Hi {user?.first_name}, your account was created with a temporary password. Please choose a secure one before you proceed.</div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="force-password-form">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Temporary password</Label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pl-9 h-11"
                data-testid="force-password-current-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-9 h-11"
                  data-testid="force-password-new-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm new password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-9 h-11"
                  data-testid="force-password-confirm-input"
                />
              </div>
            </div>
          </div>

          <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 bg-slate-50 border border-slate-200 rounded-md p-3">
            <Rule ok={checks.length} label="At least 8 characters" />
            <Rule ok={checks.upper} label="One uppercase letter" />
            <Rule ok={checks.lower} label="One lowercase letter" />
            <Rule ok={checks.digit} label="One number" />
            <Rule ok={checks.match} label="Both passwords match" />
            <Rule ok={checks.different} label="Different from temporary" />
          </ul>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-sm text-slate-500 hover:text-slate-800"
              data-testid="force-password-cancel-btn"
            >
              Sign out
            </button>
            <Button
              type="submit"
              disabled={!allOk || submitting}
              className="h-11 bg-[#0b192c] hover:bg-[#0f2544]"
              data-testid="force-password-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password & continue"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
